import { FC } from 'react'
import { Tooltip } from 'antd'
import { formatCurrency } from '@/utils/priceCalculations'

interface PricingDisplayInlineProps {
  quantity: number
  unitPrice: number
  className?: string
}

/**
 * PricingDisplayInline - แสดงราคาต่อหน่วยและราคารวมแบบ compact
 * 
 * Design System: ui-ux-pro-max (Conversion-Optimized)
 * - Colors: Primary #1E40AF, CTA #F59E0B
 * - Typography: tabular-nums for numbers, leading-relaxed
 * - UX: High contrast (text-gray-900), adequate spacing (gap-2)
 * - Pattern: Two-line stacked with clear visual hierarchy
 */
export const PricingDisplayInline: FC<PricingDisplayInlineProps> = ({
  quantity,
  unitPrice,
  className = '',
}) => {
  const totalPrice = quantity * unitPrice
  const hasQuantity = quantity > 0

  const tooltipContent = (
    <div className='text-xs leading-relaxed'>
      <div className='text-slate-200'>
        {quantity.toLocaleString()} × ฿{unitPrice.toLocaleString()}
      </div>
    </div>
  )

  return (
    <Tooltip 
      title={hasQuantity ? tooltipContent : null}
      placement='left'
      mouseEnterDelay={0.3}
    >
      <div 
        className={`
          min-w-[90px] text-right cursor-default leading-relaxed
          ${hasQuantity ? 'hover:bg-blue-50 rounded-md px-2 py-1 -mx-2 -my-1 transition-colors duration-200' : ''}
          ${className}
        `}
      >
        {/* Unit Price - muted secondary info with adequate contrast */}
        <div 
          className={`
            text-xs tabular-nums leading-relaxed
            ${hasQuantity ? 'text-slate-500' : 'text-slate-300'}
          `}
        >
          @{unitPrice.toLocaleString()}
        </div>
        
        {/* Total Price - primary emphasis with high contrast */}
        <div 
          className={`
            text-base font-semibold tabular-nums leading-tight mt-0.5
            ${hasQuantity ? 'text-blue-700' : 'text-slate-300'}
          `}
        >
          {formatCurrency(totalPrice)}
        </div>
      </div>
    </Tooltip>
  )
}

export default PricingDisplayInline